import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { Building2, ArrowLeft, Info, Calculator, Save, Send, X, Loader2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { requestsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const activityTypes = [
  'Advisory Board',
  'Symposium', 
  'Speaker Program',
  'Exhibition'
];

const countries = [
  'UAE',
  'KSA',
  'Qatar',
  'Egypt'
];

const NewRequest = ({ user }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    type: '',
    country: '',
    event_date: '',
    business_objective: '',
    venue_costs: '',
    speaker_fees: '',
    travel_costs: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateFMV = () => {
    const venue = parseFloat(formData.venue_costs) || 0;
    const speaker = parseFloat(formData.speaker_fees) || 0;
    const travel = parseFloat(formData.travel_costs) || 0;
    return venue + speaker + travel;
  };

  const handleSubmit = async (isDraft = false) => {
    if (!isDraft) {
      // Validate required fields
      if (!formData.title || !formData.type || !formData.country || !formData.event_date || !formData.business_objective) {
        toast({
          title: "Validation Error",
          description: "Please fill in all required fields",
          variant: "destructive"
        });
        return;
      }
    }

    setLoading(true);
    try {
      const requestData = {
        title: formData.title,
        type: formData.type,
        country: formData.country,
        event_date: formData.event_date,
        business_objective: formData.business_objective,
        venue_costs: parseFloat(formData.venue_costs) || 0,
        speaker_fees: parseFloat(formData.speaker_fees) || 0,
        travel_costs: parseFloat(formData.travel_costs) || 0,
        is_draft: isDraft
      };

      await requestsAPI.create(requestData, user.id);

      toast({
        title: isDraft ? "Draft Saved" : "Request Submitted",
        description: isDraft ? "Your request has been saved as a draft" : "Your request has been submitted for approval",
        variant: "default"
      });

      navigate('/dashboard');
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to submit request",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Dashboard</span>
              </Button>
              <Separator orientation="vertical" className="h-6" />
              {/* Menarini Logo */}
              <div className="bg-red-600 text-white px-2 py-1 text-sm font-bold">
                MENARINI
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">New Activity Request</h1>
                <p className="text-sm text-gray-500">Create a new approval request</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <p className="text-sm text-gray-600">Logged in as {user.name}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Activity Request Form</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Section 1: Basic Information */}
            <div className="space-y-6">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="title">Activity Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter activity title"
                    disabled={loading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Activity Type *</Label>
                  <Select value={formData.type} onValueChange={(value) => handleInputChange('type', value)} disabled={loading}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select activity type" />
                    </SelectTrigger>
                    <SelectContent>
                      {activityTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country/Region *</Label>
                  <Select value={formData.country} onValueChange={(value) => handleInputChange('country', value)} disabled={loading}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event_date">Event Date *</Label>
                  <Input
                    id="event_date"
                    type="date"
                    value={formData.event_date}
                    onChange={(e) => handleInputChange('event_date', e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Section 2: Details & Justification */}
            <div className="space-y-6">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-gray-900">Details & Justification</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="business_objective">Business Objective *</Label>
                <Textarea
                  id="business_objective"
                  value={formData.business_objective}
                  onChange={(e) => handleInputChange('business_objective', e.target.value)}
                  placeholder="Describe the business objective and rationale for this activity"
                  rows={4}
                  disabled={loading}
                />
              </div>
            </div>

            <Separator />

            {/* Section 3: Financials (FMV) */}
            <div className="space-y-6">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-gray-900">Fair Market Value (FMV) Assessment</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="venue_costs">Venue Costs</Label>
                  <Input
                    id="venue_costs"
                    type="number"
                    value={formData.venue_costs}
                    onChange={(e) => handleInputChange('venue_costs', e.target.value)}
                    placeholder="0.00"
                    disabled={loading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="speaker_fees">Speaker Fees</Label>
                  <Input
                    id="speaker_fees"
                    type="number"
                    value={formData.speaker_fees}
                    onChange={(e) => handleInputChange('speaker_fees', e.target.value)}
                    placeholder="0.00"
                    disabled={loading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="travel_costs">Travel Costs</Label>
                  <Input
                    id="travel_costs"
                    type="number"
                    value={formData.travel_costs}
                    onChange={(e) => handleInputChange('travel_costs', e.target.value)}
                    placeholder="0.00"
                    disabled={loading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="automatedFMV">
                    <span className="flex items-center space-x-2">
                      <span>Automated FMV</span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-4 w-4 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>This value will be calculated automatically based on internal financial rules</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="automatedFMV"
                      type="number"
                      value={calculateFMV()}
                      disabled
                      className="bg-gray-50"
                    />
                    <Calculator className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4">
              <Button
                variant="outline"
                onClick={handleCancel}
                className="flex items-center space-x-2"
                disabled={loading}
              >
                <X className="h-4 w-4" />
                <span>Cancel</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => handleSubmit(true)}
                className="flex items-center space-x-2"
                disabled={loading}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                <span>Save Draft</span>
              </Button>
              <Button
                onClick={() => handleSubmit(false)}
                className="bg-red-600 hover:bg-red-700 text-white flex items-center space-x-2"
                disabled={loading}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                <span>Submit for Approval</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default NewRequest;